import { useEffect, useRef, useState } from "react";

interface CountUpOptions {
  duration?: number; // ms
  start?: number;
  enabled?: boolean; // start animating when true
  decimals?: number;
}

/**
 * Smoothly counts from `start` to `end` once `enabled` becomes true.
 * Respects prefers-reduced-motion (jumps straight to the end value).
 */
export function useCountUp(end: number, options: CountUpOptions = {}) {
  const { duration = 1200, start = 0, enabled = true, decimals = 0 } = options;
  const [value, setValue] = useState(start);
  const startedRef = useRef(false);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled || startedRef.current) return;
    startedRef.current = true;

    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setValue(end);
      return;
    }

    const startTime = performance.now();
    const ascending = end >= start;
    const clamp = (n: number) =>
      ascending ? Math.min(n, end) : Math.max(n, end);
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(1, elapsed / duration);
      // easeOutCubic (monotonic, no overshoot)
      const eased = 1 - Math.pow(1 - progress, 3);
      const raw = start + (end - start) * eased;
      const rounded =
        decimals > 0 ? Number(raw.toFixed(decimals)) : Math.round(raw);
      // Hard clamp so the displayed value can never pass `end`
      // (e.g. due to rounding up near completion).
      const safe = progress >= 1 ? end : clamp(rounded);
      setValue(safe);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [enabled, end, start, duration, decimals]);

  return value;
}
